import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Alert from 'Components/Alert';
import FieldSet from 'Components/FieldSet';
import CheckInput from 'Components/Form/CheckInput';
import FormGroup from 'Components/Form/FormGroup';
import FormLabel from 'Components/Form/FormLabel';
import TextArea from 'Components/Form/TextArea';
import TextInput from 'Components/Form/TextInput';
import Button from 'Components/Link/Button';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import { kinds } from 'Helpers/Props';
import { tdarrCrud } from '../api';
import SettingsToolbar from '../components/SettingsToolbar';
import { useTdarrCollection } from '../queries';
import { getAdvancedSettings, setAdvancedSettings } from '../settings';

const notificationEvents = [
  ['notificationsServerStarted', 'Server Started'], ['notificationsNodeEvents', 'Node Connect / Disconnect'],
  ['notificationsCacheFull', 'Cache Full'], ['notificationsTranscodeSuccess', 'Transcode Success'],
  ['notificationsTranscodeError', 'Transcode Error'], ['notificationsTranscodeCancelled', 'Transcode Cancelled'],
  ['notificationsHealthcheckSuccess', 'Health Check Success'], ['notificationsHealthcheckError', 'Health Check Error'],
  ['notificationsHealthcheckCancelled', 'Health Check Cancelled'], ['notificationsRequireReview', 'Review Required'],
  ['notificationsServerUpdateReady', 'Server Update Ready']
] as const;

type NotificationEventKey = typeof notificationEvents[number][0];
type NotificationForm = Record<NotificationEventKey, boolean> & { notificationsDiscordWebhook: string; notificationsCustomText: string };

const emptyForm: NotificationForm = {
  notificationsDiscordWebhook: '', notificationsCustomText: '', notificationsServerStarted: false,
  notificationsNodeEvents: false, notificationsCacheFull: false, notificationsTranscodeSuccess: false,
  notificationsTranscodeError: false, notificationsTranscodeCancelled: false, notificationsHealthcheckSuccess: false,
  notificationsHealthcheckError: false, notificationsHealthcheckCancelled: false, notificationsRequireReview: false,
  notificationsServerUpdateReady: false
};

export default function Notifications() {
  const queryClient = useQueryClient();
  const settings = useTdarrCollection('SettingsGlobalJSONDB');
  const global = settings.data?.[0];
  const [form, setForm] = useState(emptyForm);
  const [savedForm, setSavedForm] = useState(emptyForm);
  const [showWebhook, setShowWebhook] = useState(false);
  const [advanced, setAdvanced] = useState(getAdvancedSettings);
  useEffect(() => {
    if (!global) return;
    const next = { ...emptyForm };
    next.notificationsDiscordWebhook = String(global.notificationsDiscordWebhook ?? '');
    next.notificationsCustomText = String(global.notificationsCustomText ?? '');
    notificationEvents.forEach(([key]) => { next[key] = Boolean(global[key]); });
    setForm(next);
    setSavedForm(next);
  }, [global]);
  const hasPendingChanges = JSON.stringify(form) !== JSON.stringify(savedForm);
  const save = useMutation({
    mutationFn: () => tdarrCrud({ collection: 'SettingsGlobalJSONDB', mode: 'update', docID: 'globalsettings', obj: form }),
    onSuccess: async () => {
      setSavedForm(form);
      await queryClient.invalidateQueries({ queryKey: ['tdarr', 'collection', 'SettingsGlobalJSONDB'] });
    }
  });
  const test = useMutation({
    mutationFn: async () => {
      if (!form.notificationsDiscordWebhook.trim()) throw new Error('Webhook required');
      const response = await fetch(form.notificationsDiscordWebhook.trim(), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content: 'Tdarr test message' }) });
      if (!response.ok && response.status !== 204) throw new Error(`Discord returned ${response.status}`);
    }
  });
  const toggleAdvanced = () => {
    setAdvanced((current) => {
      setAdvancedSettings(!current);
      return !current;
    });
  };
  return <PageContent title="Notifications">
    <SettingsToolbar advancedSettings={advanced} onAdvancedSettingsPress={toggleAdvanced} isSaving={save.isPending} hasPendingChanges={hasPendingChanges} isSaveDisabled={!global} onSavePress={() => save.mutate()} />
    <PageContentBody><FieldSet legend="Discord">
      {settings.isError && <Alert kind={kinds.DANGER}>Unable to load Tdarr notification settings.</Alert>}
      {save.isSuccess && <Alert kind={kinds.SUCCESS}>Notification settings saved.</Alert>}
      {save.isError && <Alert kind={kinds.DANGER}>Notification settings could not be saved.</Alert>}
      <FormGroup size="large"><FormLabel name="notificationsDiscordWebhook">Discord Webhook URL</FormLabel><TextInput<string> type={showWebhook ? 'text' : 'password'} name="notificationsDiscordWebhook" value={form.notificationsDiscordWebhook} placeholder="https://discord.com/api/webhooks/..." onChange={({ value }) => setForm((current) => ({ ...current, notificationsDiscordWebhook: String(value) }))} /></FormGroup>
      <FormGroup><FormLabel>Webhook Actions</FormLabel><div><Button size="small" onPress={() => setShowWebhook((value) => !value)}>{showWebhook ? 'Hide' : 'Show'}</Button>{' '}<Button size="small" isDisabled={!form.notificationsDiscordWebhook.trim() || test.isPending} onPress={() => test.mutate()}>{test.isPending ? 'Testing…' : 'Test'}</Button></div></FormGroup>
      {test.isSuccess && <Alert kind={kinds.SUCCESS}>Discord test succeeded.</Alert>}
      {test.isError && <Alert kind={kinds.DANGER}>Discord test failed. Check the webhook URL and browser network access.</Alert>}
    </FieldSet>
    <FieldSet legend="Send Notifications For">
      {notificationEvents.map(([key, label]) => <FormGroup key={key}><FormLabel name={key}>{label}</FormLabel><CheckInput name={key} value={form[key]} onChange={({ value }) => setForm((current) => ({ ...current, [key]: Boolean(value) }))} /></FormGroup>)}
    </FieldSet>
    {advanced && <FieldSet legend="Custom Notification Text">
      <FormGroup size="large"><FormLabel name="notificationsCustomText">Footer Text</FormLabel><TextArea name="notificationsCustomText" value={form.notificationsCustomText} placeholder={'Server: My Tdarr Server\nDashboard: https://tdarr.example.com'} onChange={({ value }) => setForm((current) => ({ ...current, notificationsCustomText: value }))} /></FormGroup>
    </FieldSet>}</PageContentBody>
  </PageContent>;
}
